import React, { useCallback, useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@mui/material";
import { CloudUpload, Delete, Close, GetApp } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { CheckCircle } from "@mui/icons-material";
import { LanguageContext } from "../context/LanguageContext";

export default function UploadFilesModal({
  open,
  onClose,
  files,
  setFiles,
  maxSize = 10 * 1024 * 1024, // 10MB
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { translate } = useContext(LanguageContext);

  // ==============================
  // simule l’upload dès qu’un fichier est ajouté
  const simulateUpload = async (index) => {
    for (let p = 0; p <= 100; p = 10) {
      await new Promise((r) => setTimeout(r, 50));
      setFiles((f) =>
        f.map((x, j) =>
          j === index ? { ...x, progress: p, uploaded: p === 100 } : x
        )
      );
    }
  };
  // ==============================

  const onDrop = useCallback(
    (accepted) => {
      setFiles((f) => {
        const out = [
          ...f,
          ...accepted.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
            progress: 0,
            uploaded: false,
          })),
        ];
        // on simule l’upload de chaque nouveau
        accepted.forEach((_, i) => simulateUpload(f.length + i));
        return out;
      });
    },
    [setFiles]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      maxSize,
    });

  const startUpload = async () => {
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      // simulate upload
      for (let p = 0; p <= 100; p += 10) {
        await new Promise((r) => setTimeout(r, 50));
        setFiles((f) => f.map((x, j) => (j === i ? { ...x, progress: p } : x)));
      }
    }
    setUploading(false);
  };

  const remove = (idx) => {
    setFiles((f) => f.filter((_, j) => j !== idx));
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {translate("UploadFiles")}
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Zone drag & drop */}
        <div
          {...getRootProps()}
          style={{
            border: "2px dashed #1976d2",
            borderRadius: 8,
            padding: 20,
            textAlign: "center",
            marginBottom: 16,
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: "#1976d2" }} />
          <div style={{ marginTop: 8 }}>
            {isDragActive
              ? translate("DropFilesHere")
              : translate("DragAndDropOrBrowse")}
          </div>
          <div style={{ color: "red", marginTop: 4 }}>
            {translate("MaxFileSize")} {Math.round(maxSize / 1024 / 1024)}MB
          </div>
        </div>

        <List
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            overflowX: "auto",
            whiteSpace: "nowrap",
            p: 0,
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 3,
            },
          }}
        >
          {files.map((f, idx) => (
            <ListItem
              key={idx}
              // passer le ListItem en flex row
              sx={{
                display: "flex",
                alignItems: "center",
                width: "150px",
                justifyContent: "space-between",
                border: "2px dashed lightgrey",
                borderRadius: "10px",
                gap: 2,
              }}
              // secondaryAction place automatiquement à l'extrême droite
              secondaryAction={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <IconButton
                    edge="end"
                    size="small"
                    component="a"
                    href={f.file ? f.preview : f.url}
                    target="_blank"
                  >
                    <GetApp />
                  </IconButton>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => remove(idx)}
                  >
                    <Delete color="error" />
                  </IconButton>
                </div>
              }
            >
              {/* Vignette cliquable */}
              <ListItemAvatar>
                <IconButton
                  component="a"
                  href={f.file ? f.preview : f.url}
                  target="_blank"
                  sx={{ p: 0 }}
                >
                  <Avatar
                    variant="square"
                    src={f.preview || f.url}
                    sx={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                    }}
                  />
                </IconButton>
              </ListItemAvatar>

              {/* Barre de progression si upload en cours */}
              {uploading && (
                <LinearProgress
                  variant="determinate"
                  value={f.progress}
                  sx={{ width: 100, ml: 2 }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {translate("Done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
